import { useMemo } from 'react';
import type { QuickTaggedTemplateWithPlural } from '@withjoy/tq';
import { useTranslation } from '@shared/core';
import { useScenario } from '@apps/acquisitionFunnels/createWedding/hooks';

interface HeroTextConfig
  extends Readonly<{
    createAccountHeroText: string;
    createWeddingHeroText: string;
  }> {}

const createHeroText = (text: string): HeroTextConfig => ({
  createAccountHeroText: text,
  createWeddingHeroText: text,
});

const getHeroTextMap = (tq: QuickTaggedTemplateWithPlural): Readonly<Record<ReturnType<typeof useScenario>, HeroTextConfig>> => ({
  'save-the-date': createHeroText(tq`Smarter Save the Dates, coming right up.`),
  invitation: createHeroText(tq`Smarter invitations, coming right up.`),
  'wedding-website': createHeroText(tq`Let's get ready to get planning`),
  'guest-list': createHeroText(tq`Start building your smarter guest list.`),
  registry: createHeroText(tq`Start building your all-in-one registry.`),
  app: createHeroText(tq`The smarter app for you and your guests.`),
  'imagine-your-card': createHeroText(tq`Sign up for free to finish creating your card`),
  default: {
    createAccountHeroText: tq`Hi! Let's get started.`,
    createWeddingHeroText: tq`Let's get ready to get planning`,
  },
});

const getHeroTextForScenario = (scenario: ReturnType<typeof useScenario>, tq: QuickTaggedTemplateWithPlural): HeroTextConfig => {
  const heroTextMap = getHeroTextMap(tq);
  return heroTextMap[scenario] ?? heroTextMap.default;
};

export const useHeroTextByScenario = (): Readonly<HeroTextConfig> => {
  const scenario = useScenario();
  const { tq } = useTranslation('createWedding');

  return useMemo(() => getHeroTextForScenario(scenario, tq), [scenario, tq]);
};
